<template>
    <DsModal
        class="setup-complete-modal"
        show-close
        :is-open="isOpen"
        @close="$emit('close')"
    >
        <template #title>
            <div class="modal-title">
                Keap Marketing Number setup
            </div>
        </template>
        <section class="content">
            <ds-illustration class="success-illustration" name="success-confetti-color" />

            <h2 class="title">
                Your Keap Marketing Number is ready!
            </h2>

            <span class="phone-number">{{ phoneNumber }}</span>

            <span class="description">This is your assigned Keap Marketing Number. Your recipients will see this number every time you send an automated or broadcast text message.</span>

            <ds-filled-button class="action-button" @click="$emit('close')">
                Continue
            </ds-filled-button>
        </section>

        <section class="resources">
            <h3 class="review-title">
                Review this important info
            </h3>

            <span class="review-description">Please review the resources below, including Telephone Consumer Protection Act policies. They cover permissions, best practices, opt-in rules, and more. Find these policies and manage your preferences in your
                <ds-link trailing-icon="external-link" @click="handleSettingsClick">Keap Marketing Number settings </ds-link>
            </span>

            <a
                :href="complianceLink"
                class="links"
                rel="noopener noreferer"
                target="_blank"
            >
                Guide to U.S. SMS Compliance

                <ds-icon class="link-icon" name="external-link" />
            </a>
            <span class="resources-subtext">Twilio</span>
            <a
                :href="bestPracticesLink"
                class="links"
                rel="noopener noreferer"
                target="_blank"
            >
                Messaging Principles & Best Practices

                <ds-icon class="link-icon" name="external-link" />
            </a>
            <span class="resources-subtext">Cellular Telecommunications Industry Association</span>

            <a
                :href="tcpaLink"
                class="links"
                rel="noopener noreferer"
                target="_blank"
            >
                Telephone Consumer Protection Act

                <ds-icon class="link-icon" name="external-link" />
            </a>
            <span class="resources-subtext">TCPA</span>
        </section>

        <text-marketing-settings-modal
            v-if="isSmsBroadcastSettingsModalOpen"
            :is-billing-changes-enabled="isBillingChangesEnabled"
            :account="account"
            @close="isSmsBroadcastSettingsModalOpen = false"
            @update="handleAccountUpdate"
        />
    </DsModal>
</template>
<script>
import TextMarketingSettingsModal from '../TextMarketingSettingsModal';
import { formatPhone } from '../phone.util';

export default {
    name: 'SmsSetupCompleteModal',

    components: { TextMarketingSettingsModal },

    props: {
        account: {
            type: Object,
            default: () => ({}),
        },
        fromNumber: String,
        isOpen: Boolean,
        isBillingChangesEnabled: Boolean,
    },

    data() {
        return {
            complianceLink: 'https://www.twilio.com/learn/call-and-text-marketing/guide-to-us-sms-compliance',
            bestPracticesLink: 'https://www.ctia.org/the-wireless-industry/industry-commitments/messaging-interoperability-sms-mms',
            tcpaLink: 'https://www.fdic.gov/regulations/compliance/manual/8/viii-5.1.pdf',
            isSmsBroadcastSettingsModalOpen: false,
        };
    },

    computed: {
        phoneNumber() {
            return `1 ${formatPhone(this.account.phoneNumber)}`;
        },
    },

    methods: {
        handleAccountUpdate(account) {
            this.$emit('update', account);
        },

        handleSettingsClick() {
            this.isSmsBroadcastSettingsModalOpen = true;
        },
    },
};
</script>

<style lang="scss" scoped>
    .links {
        font-size: px-to-rem(14px);
        color: $color-black;
        padding-bottom: $spacing-025;
    }

    .link-icon {
        --icon-size: #{1rem};
    }

    .resources-subtext {
        color: $color-text-disabled;
        padding-bottom: $spacing-200;
    }

    .setup-complete-modal {
        --modal-padding: 0;
    }

    .content {
        padding: 0 $spacing-300 $spacing-300;
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
    }

    .title {
        line-height: 2.25rem;
        padding: 0 $spacing-600;
        margin-bottom: $spacing-300;
        width: 70%;
    }

    .success-illustration {
        margin: $spacing-400 0;
    }

    .modal-title {
        font-weight: $font-weight-bold;
    }

    .phone-number {
        font-size: $font-size-xl;
        font-weight: 600;
        margin-bottom: $spacing-200;
    }

    .description {
        color: $color-ink-800;
        padding: 0 $spacing-600 $spacing-300;
        font-size: $font-size-sm;
    }

    .resources {
        display: flex;
        flex-direction: column;
        padding: $spacing-300 $spacing-400 $spacing-100;
        background-color: $color-orange-050;
        background-clip: border-box;
        border-radius: 0 0 $border-radius $border-radius;
    }

    .protect-yourself {
        margin-bottom: $spacing-050;
        color: $color-gray-darker;
    }

    .review-title {
        padding-bottom: $spacing-200;
        font-weight: 500;
    }

    .review-description {
        line-height: $line-height-md;
        padding: 0 0 $spacing-200;
    }

    .link-list {
        @include padding-start($spacing-200);

        list-style: disc outside;
        --icon-size: #{px-to-rem(13.33px)};
    }
</style>
